import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Features } from 'src/app/services/auth/features';
import { RealtimeService } from 'src/app/services/realtime/realtime.service';
import { WebsocketService } from 'src/app/services/websocket/websocket.service';
import { Project } from '../classes/project.class';
import { User } from '../classes/user.class';
import { ChainEntry } from '../pages/project-executor-planning/activity-constants';
import { ApiResponse, ApiService } from './api/api.service';

export interface ProjectExecutorPlanningEntry {
    id: number;
    afas_project_id: string;
    user_id: number;
    date: string;
    activity: string;
    notes?: string;
    created_at: string;
    updated_at: string;
    project: Project;
    user: User;
    chain: ChainEntry[] | null;
}

export interface AsphaltPerformerPlanningEntry {
    planning_id: number;
    user_id: number;
    date: string; // YYYY-MM-DD
    afas_project_id: string;
    project_name: string;
    project: Project | null;
    entitytype_key: string;
    start_time?: string;
    end_time?: string | null;
}

export interface CreateExecutorPlanningRequest {
    executorId: number;
    projectId: string;
    date: string;
    activity: string;
    notes?: string;
    allowDuplicates?: boolean;
}

export interface UpdateExecutorPlanningRequest {
    id: number;
    executorId: number;
    projectId: string;
    date: string;
    activity: string;
    notes?: string;
}

export interface BulkUpdateExecutorPlanningRequest {
    id: number;
    executorId?: number;
    projectId?: string;
    date?: string;
    activity?: string;
    notes?: string;
    allowDuplicates?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ExecutorPlanningService extends RealtimeService<ProjectExecutorPlanningEntry> {
    private readonly API_BASE_PATH = 'executor-schedule';

    constructor(
        private readonly apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, Features.ExecutorSchedule);
    }

    itemInDaterange(planning: ProjectExecutorPlanningEntry, fromDate: Date, toDate: Date): boolean {
        const planningDate = new Date(planning.date);
        return planningDate >= fromDate && planningDate <= toDate;
    }

    public getFilteredList(fromDate: Date, toDate: Date, filterId?: any): Observable<ProjectExecutorPlanningEntry[]> {
        return super.getFilteredList(fromDate, toDate).pipe(
            map(entries => {
                return entries.filter(
                    entry => this.itemInDaterange(entry, fromDate, toDate) && (!filterId || entry.user_id === filterId)
                );
            })
        );
    }

    /**
     * Get executor planning entries by date range
     */
    public getPlanningByDate(startDate: Date, endDate: Date): Observable<ProjectExecutorPlanningEntry[]> {
        const params = {
            startDate: this.formatDate(startDate),
            endDate: this.formatDate(endDate),
        };

        return this.apiService
            .getCallObs<ApiResponse<ProjectExecutorPlanningEntry[]>>(`${this.API_BASE_PATH}`, params)
            .pipe(map(response => response.data || []));
    }

    /**
     * Get executor planning entries for a specific executor
     */
    public getPlanningByExecutor(
        executorId: number,
        startDate?: Date,
        endDate?: Date
    ): Observable<ProjectExecutorPlanningEntry[]> {
        const params: any = {};

        if (startDate) {
            params.startDate = this.formatDate(startDate);
        }
        if (endDate) {
            params.endDate = this.formatDate(endDate);
        }

        return this.apiService
            .getCallObs<ApiResponse<ProjectExecutorPlanningEntry[]>>(
                `${this.API_BASE_PATH}/executor/${executorId}`,
                params
            )
            .pipe(map(response => response.data || []));
    }

    /**
     * Get a specific planning entry by ID
     */
    public getPlanningById(id: number): Observable<ProjectExecutorPlanningEntry> {
        return this.apiService
            .getCallObs<ApiResponse<ProjectExecutorPlanningEntry>>(`${this.API_BASE_PATH}/${id}`)
            .pipe(map(response => response.data));
    }

    /**
     * Create a new planning entry
     */
    public createPlanning(request: CreateExecutorPlanningRequest): Observable<ProjectExecutorPlanningEntry> {
        const payload = {
            afas_project_id: request.projectId,
            user_id: request.executorId,
            date: request.date,
            activity: request.activity,
            ...(request.notes && { notes: request.notes }),
            ...(request.allowDuplicates !== undefined && { allow_duplicates: request.allowDuplicates }),
        };

        return this.apiService
            .postCallObserver<ApiResponse<ProjectExecutorPlanningEntry>>(this.API_BASE_PATH, payload)
            .pipe(map(response => response.data));
    }

    /**
     * Create multiple planning entries at once
     */
    public createBulkPlanning(requests: CreateExecutorPlanningRequest[]): Observable<ProjectExecutorPlanningEntry[]> {
        const payload = {
            schedules: requests.map(request => ({
                afas_project_id: request.projectId,
                user_id: request.executorId,
                date: request.date,
                activity: request.activity,
                ...(request.notes && { notes: request.notes }),
            })),
            ...(requests.some(r => r.allowDuplicates) && { allow_duplicates: true }),
        };

        return this.apiService
            .postCallObserver<ApiResponse<ProjectExecutorPlanningEntry[]>>(`${this.API_BASE_PATH}/bulk`, payload)
            .pipe(map(response => response.data || []));
    }

    /**
     * Update an existing planning entry
     */
    public updatePlanning(request: UpdateExecutorPlanningRequest): Observable<ProjectExecutorPlanningEntry> {
        const payload = {
            afas_project_id: request.projectId,
            user_id: request.executorId,
            date: request.date,
            activity: request.activity,
            ...(request.notes && { notes: request.notes }),
            _method: 'PUT',
        };

        return this.apiService
            .postCallObserver<ApiResponse<ProjectExecutorPlanningEntry>>(`${this.API_BASE_PATH}/${request.id}`, payload)
            .pipe(map(response => response.data));
    }

    /**
     * Update multiple planning entries at once
     */
    public updateBulkPlanning(
        requests: BulkUpdateExecutorPlanningRequest[]
    ): Observable<ProjectExecutorPlanningEntry[]> {
        const payload = {
            schedules: requests.map(request => {
                const scheduleUpdate: any = {
                    id: request.id,
                };

                if (request.projectId !== undefined) {
                    scheduleUpdate.afas_project_id = request.projectId;
                }
                if (request.executorId !== undefined) {
                    scheduleUpdate.user_id = request.executorId;
                }
                if (request.date !== undefined) {
                    scheduleUpdate.date = request.date;
                }
                if (request.activity !== undefined) {
                    scheduleUpdate.activity = request.activity;
                }
                if (request.notes !== undefined) {
                    scheduleUpdate.notes = request.notes;
                }

                return scheduleUpdate;
            }),
            ...(requests.some(r => r.allowDuplicates) && { allow_duplicates: true }),
        };

        return this.apiService
            .putCall$<ProjectExecutorPlanningEntry[]>(`${this.API_BASE_PATH}/bulk`, payload)
            .pipe(map(response => response.data || []));
    }

    /**
     * Delete a planning entry
     */
    public deletePlanning(id: number): Observable<void> {
        return this.apiService.deleteCall$<ApiResponse<void>>(`${this.API_BASE_PATH}/${id}`).pipe(map(() => void 0));
    }

    /**
     * Delete multiple planning entries at once
     */
    public deleteBulkPlanning(
        ids: number[]
    ): Observable<{ deleted_count: number; deleted_schedules: ProjectExecutorPlanningEntry[] }> {
        return this.apiService
            .postCallObserver<
                ApiResponse<{ deleted_count: number; deleted_schedules: ProjectExecutorPlanningEntry[] }>
            >(`${this.API_BASE_PATH}/delete-bulk`, { ids })
            .pipe(map(response => response.data || { deleted_count: 0, deleted_schedules: [] }));
    }

    /**
     * Get planning entries for multiple executors in a date range
     */
    public getPlanningForExecutors(
        executorIds: number[],
        startDate: Date,
        endDate: Date
    ): Observable<ProjectExecutorPlanningEntry[]> {
        // Since the backend doesn't support multiple executor IDs in one call,
        // we'll call the date range endpoint and filter on the frontend
        return this.getPlanningByDate(startDate, endDate).pipe(
            map(entries => entries.filter(entry => executorIds.includes(entry.user_id)))
        );
    }

    /**
     * Helper method to format date for API calls
     */
    private formatDate(date: Date): string {
        return date.toISOString().split('T')[0];
    }

    /**
     * Get asphalt planning entries where the given users are the asphalt performer
     */
    public getAsphaltPerformerPlanning(startDate: Date, endDate: Date): Observable<AsphaltPerformerPlanningEntry[]> {
        const params = {
            fromDate: this.formatDate(startDate),
            toDate: this.formatDate(endDate),
        };
        return this.apiService
            .getCallObs<ApiResponse<AsphaltPerformerPlanningEntry[]>>('planning/asphalt-performer', params)
            .pipe(map(response => response.data || []));
    }
}
